import React, { useState, useEffect, useCallback } from 'react';
import { listUsers, disableUser, enableUser, resetUserPassword, exportUsersReport, bulkUpdateFeatureFlags } from '../../services/adminApi';
import UserTable from '../components/UserTable';
import { notify, confirmAsync } from '../../utils/notify';
import './UserList.css';

function UserList() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkAction, setBulkAction] = useState('');

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await listUsers(page, pageSize, search || null);
      setUsers(response.data);
      // Note: API should return total count for proper pagination
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleDisable = async (userId) => {
    if (!(await confirmAsync({ message: 'Are you sure you want to disable this user?', danger: true }))) {
      return;
    }
    try {
      await disableUser(userId);
      loadUsers();
    } catch (err) {
      notify.error('Failed to disable user');
    }
  };

  const handleEnable = async (userId) => {
    if (!(await confirmAsync({ message: 'Are you sure you want to enable this user?' }))) {
      return;
    }
    try {
      await enableUser(userId);
      loadUsers();
    } catch (err) {
      notify.error('Failed to enable user');
    }
  };

  const handleResetPassword = async (userId) => {
    if (!(await confirmAsync({ message: 'Send password reset email to this user?' }))) {
      return;
    }
    try {
      await resetUserPassword(userId);
      notify.success('Password reset email sent');
    } catch (err) {
      notify.error('Failed to send password reset email');
    }
  };


  const handleExport = async (format) => {
    try {
      await exportUsersReport(format);
    } catch (err) {
      // ``exportUsersReport`` decodes blob error bodies, so ``err.message``
      // here is the real backend detail (auth, server error, content-type
      // mismatch). Surface it so a 401/500 is actionable instead of opaque.
      console.error('Failed to export users:', err);
      notify.error(err?.message || 'Failed to export users');
    }
  };

  const handleSelectUser = (userId, checked) => {
    setSelectedUsers(prev =>
      checked ? [...prev, userId] : prev.filter(id => id !== userId)
    );
  };

  const handleSelectAll = (checked) => {
    setSelectedUsers(checked ? users.map(u => u.id) : []);
  };

  const handleBulkFeatureFlag = async () => {
    if (!selectedUsers.length || !bulkAction) return;

    const flagMap = {
      enable_socials: { can_connect_socials: true },
      disable_socials: { can_connect_socials: false, can_connect_instagram: false, can_connect_facebook: false, can_connect_tiktok: false },
      enable_instagram: { can_connect_instagram: true },
      disable_instagram: { can_connect_instagram: false },
      enable_facebook: { can_connect_facebook: true },
      disable_facebook: { can_connect_facebook: false },
      enable_tiktok: { can_connect_tiktok: true },
      disable_tiktok: { can_connect_tiktok: false },
      enable_posts: { can_generate_posts: true },
      disable_posts: { can_generate_posts: false },
      enable_all: { can_connect_socials: true, can_connect_instagram: true, can_connect_facebook: true, can_connect_tiktok: true, can_generate_posts: true },
      disable_all: { can_connect_socials: false, can_connect_instagram: false, can_connect_facebook: false, can_connect_tiktok: false, can_generate_posts: false },
    };
    const flags = flagMap[bulkAction];
    if (!flags) return;

    const actionLabel = bulkAction.replace(/_/g, ' ');
    if (!(await confirmAsync({ message: `Apply "${actionLabel}" to ${selectedUsers.length} user(s)?` }))) return;

    try {
      await bulkUpdateFeatureFlags(selectedUsers, flags);
      setSelectedUsers([]);
      setBulkAction('');
      loadUsers();
    } catch (err) {
      notify.error('Failed to update feature flags');
    }
  };

  return (
    <div className="user-list">
      <div className="user-list-header">
        <h2>User Management</h2>
        <div className="export-buttons">
          <button className="btn-export" onClick={() => handleExport('csv')}>
            Export CSV
          </button>
          <button className="btn-export" onClick={() => handleExport('json')}>
            Export JSON
          </button>
        </div>
      </div>
      <div className="user-list-search">
        <input
          type="text"
          placeholder="Search users..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
      </div>

      {/* Bulk Feature Flags */}
      {selectedUsers.length > 0 && (
        <div className="bulk-actions-bar">
          <span className="bulk-count">{selectedUsers.length} selected</span>
          <select
            className="bulk-action-select"
            value={bulkAction}
            onChange={(e) => setBulkAction(e.target.value)}
          >
            <option value="">Select action...</option>
            <optgroup label="Social Accounts (Master)">
              <option value="enable_socials">✅ Enable Connect Socials (all)</option>
              <option value="disable_socials">❌ Disable Connect Socials (all)</option>
            </optgroup>
            <optgroup label="Per-Platform Access">
              <option value="enable_instagram">📸 Enable Instagram</option>
              <option value="disable_instagram">📸 Disable Instagram</option>
              <option value="enable_facebook">📘 Enable Facebook</option>
              <option value="disable_facebook">📘 Disable Facebook</option>
              <option value="enable_tiktok">🎵 Enable TikTok</option>
              <option value="disable_tiktok">🎵 Disable TikTok</option>
            </optgroup>
            <optgroup label="Post Generation">
              <option value="enable_posts">✅ Enable Generate Posts</option>
              <option value="disable_posts">❌ Disable Generate Posts</option>
            </optgroup>
            <optgroup label="All Features">
              <option value="enable_all">✅ Enable All Features</option>
              <option value="disable_all">❌ Disable All Features</option>
            </optgroup>
          </select>
          <button
            className="btn-bulk-apply"
            disabled={!bulkAction}
            onClick={handleBulkFeatureFlag}
          >
            Apply
          </button>
          <button
            className="btn-bulk-cancel"
            onClick={() => { setSelectedUsers([]); setBulkAction(''); }}
          >
            Clear
          </button>
        </div>
      )}

      <UserTable
        users={users}
        onDisable={handleDisable}
        onEnable={handleEnable}
        onResetPassword={handleResetPassword}
        loading={loading}
        selectedUsers={selectedUsers}
        onSelectUser={handleSelectUser}
        onSelectAll={handleSelectAll}
      />
      <div className="pagination">
        <button
          disabled={page === 1}
          onClick={() => setPage(page - 1)}
        >
          Previous
        </button>
        <span>Page {page}</span>
        <button
          disabled={users.length < pageSize}
          onClick={() => setPage(page + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}

export default UserList;

